import dataLayer from './data-layer';
import {getCanonicalItemList} from './data-layer-constants';

const CAROUSEL_IMPRESSION_DELAY = 300;
const PRODUCT_VISIBILITY_THRESHOLD = 0.1;

function meetsVisibilityThreshold ( itemRect, listRect ) {
    const viewportRight = window.innerWidth || document.documentElement.clientWidth;
    const viewportBottom = window.innerHeight || document.documentElement.clientHeight;
    const left = Math.max( listRect.left, 0 );
    const top = Math.max( listRect.top, 0 );
    const right = Math.min( listRect.right, viewportRight );
    const bottom = Math.min( listRect.bottom, viewportBottom );
    const intersectionWidth = Math.max(
        0,
        Math.min( itemRect.right, right ) - Math.max( itemRect.left, left )
    );
    const intersectionHeight = Math.max(
        0,
        Math.min( itemRect.bottom, bottom ) - Math.max( itemRect.top, top )
    );
    const itemWidth = itemRect.width || itemRect.right - itemRect.left;
    const itemHeight = itemRect.height || itemRect.bottom - itemRect.top;
    const itemArea = itemWidth * itemHeight;

    return itemArea > 0 && (intersectionWidth * intersectionHeight) / itemArea >= PRODUCT_VISIBILITY_THRESHOLD;
}

function getVisibilityRect ( listElement ) {
    const carouselViewport = listElement.classList.contains( 'carousel' ) &&
        listElement.querySelector( '.carousel-list-container' );

    return (carouselViewport || listElement).getBoundingClientRect();
}

export function getVisibleProductItems ( listElement ) {
    const listRect = getVisibilityRect( listElement );
    const products = new Map();

    listElement.querySelectorAll( '[data-track-product]' ).forEach( element => {
        const itemData = {...element.dataset};
        const itemId = itemData.itemId || itemData.sku;

        if ( !itemId || products.has( itemId ) ||
            !meetsVisibilityThreshold( element.getBoundingClientRect(), listRect ) ) {
            return;
        }

        products.set( itemId, itemData );
    } );

    return Array.from( products.values() );
}

function isVisibleWithinList ( product, listElement ) {
    return meetsVisibilityThreshold( product.element.getBoundingClientRect(), getVisibilityRect( listElement ) );
}

export function trackProductLists (
    root = document,
    facade = dataLayer,
    createObserver = ( callback, options ) => new IntersectionObserver( callback, options )
) {
    const lists = Array.from( root.querySelectorAll( '[data-track-product-list]' ) )
        .map( element => ( {
            element,
            itemList: getCanonicalItemList( element.dataset.itemListId, element.dataset.itemListName )
        } ) )
        .filter( ( {itemList} ) => itemList != null );

    if ( lists.length === 0 ) {
        return null;
    }

    const trackedProducts = lists.flatMap( list => Array.from(
        list.element.querySelectorAll( '[data-track-product]' )
    ).map( element => {
        const itemData = {...element.dataset};
        const itemId = itemData.itemId || itemData.sku;

        return itemId ? {
            element,
            itemData,
            itemKey: `${itemId}:${itemData.index || ''}`,
            list
        } : null;
    } ).filter( product => product != null ) );

    if ( trackedProducts.length === 0 ) {
        return null;
    }

    const productMap = new Map( trackedProducts.map( product => [product.element, product] ) );
    const productsByList = new Map( lists.map( list => [
        list.element,
        trackedProducts.filter( product => product.list.element === list.element )
    ] ) );
    const itemListMap = new Map( lists.map( list => [list.element, list.itemList] ) );
    const reportedItemsByList = new Map( lists.map( list => [list.element, new Set()] ) );
    const carouselImpressionTimers = new Map();
    let observer;

    const reportProducts = ( listElement, products ) => {
        const reportedItems = reportedItemsByList.get( listElement );
        const newlyVisibleProducts = [];

        products.forEach( product => {
            if ( reportedItems.has( product.itemKey ) ) {
                return;
            }

            reportedItems.add( product.itemKey );
            newlyVisibleProducts.push( product.itemData );
            productsByList.get( listElement )
                .filter( listProduct => listProduct.itemKey === product.itemKey )
                .forEach( listProduct => observer.unobserve( listProduct.element ) );
        } );

        if ( newlyVisibleProducts.length > 0 ) {
            facade.product.sendProductListView( newlyVisibleProducts, itemListMap.get( listElement ) );
        }
    };

    const scheduleCarouselImpression = listElement => {
        clearTimeout( carouselImpressionTimers.get( listElement ) );
        carouselImpressionTimers.set( listElement, setTimeout( () => {
            const visibleProducts = productsByList.get( listElement )
                .filter( product => isVisibleWithinList( product, listElement ) );

            reportProducts( listElement, visibleProducts );
            carouselImpressionTimers.delete( listElement );
        }, CAROUSEL_IMPRESSION_DELAY ) );
    };

    observer = createObserver( entries => {
        const visibleByList = new Map();

        entries.forEach( entry => {
            if ( !entry.isIntersecting ) {
                return;
            }

            const product = productMap.get( entry.target );
            if ( !product ) {
                return;
            }

            if ( product.list.element.classList.contains( 'carousel' ) ) {
                scheduleCarouselImpression( product.list.element );
                return;
            }

            const visibleProducts = visibleByList.get( product.list.element ) || [];
            visibleProducts.push( product );
            visibleByList.set( product.list.element, visibleProducts );
        } );

        visibleByList.forEach( ( products, listElement ) => {
            reportProducts( listElement, products );
        } );
    }, {threshold: [PRODUCT_VISIBILITY_THRESHOLD]} );

    trackedProducts.forEach( ( {element} ) => observer.observe( element ) );
    return observer;
}
